/**
 * @description Tenant list page for admin portal
 *
 * Key features:
 * - Table of all tenants (SOs) with key stats
 * - Search by name or owner email
 * - Status filter (all, active, inactive)
 * - Sortable columns (click header to toggle asc/desc)
 * - "Create" primary action (wizard titles stay on TenantCreate)
 * - Click row navigates to tenant detail
 *
 * @dependencies
 * - adminApi: getTenants() — LynxPay only (channel_partner_id IS NULL)
 * - AdminListTable + lynxPaySalesOrgColumns (Features optional; no Hosting)
 * - react-router-dom: useNavigate
 * - lucide-react: icons
 *
 * @notes
 * - Data shape from GET /api/admin/tenants:
 *   { id, organizationName, contactEmail, wristbandTenantId, isActive, createdAt,
 *     owner: { email, firstName, lastName },
 *     featureCount: { total, enabled },
 *     merchantCount }
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Building2, AlertCircle } from 'lucide-react';
import { getTenants } from '../../services/api/adminApi';
import AdminListTable from '../../components/admin-list/AdminListTable';
import { lynxPaySalesOrgColumns } from '../../components/admin-list/adminListColumns';

function TenantList() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortKey, setSortKey] = useState('organizationName');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const result = await getTenants();
        if (result.success) {
          setTenants(result.data);
        }
      } catch (err) {
        setError(err.response?.data?.error?.message || 'Failed to load tenants');
      } finally {
        setLoading(false);
      }
    };

    fetchTenants();
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Filter and sort tenants
  const filtered = useMemo(() => {
    let result = tenants.filter((t) => {
      const matchesSearch = !search ||
        t.organizationName.toLowerCase().includes(search.toLowerCase()) ||
        (t.owner?.email || '').toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && t.isActive) ||
        (statusFilter === 'inactive' && !t.isActive);

      return matchesSearch && matchesStatus;
    });

    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortKey) {
        case 'organizationName':
          aVal = a.organizationName.toLowerCase();
          bVal = b.organizationName.toLowerCase();
          break;
        case 'merchantCount':
          aVal = a.merchantCount || 0;
          bVal = b.merchantCount || 0;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [tenants, search, statusFilter, sortKey, sortDir]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-800 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-gray-900 font-medium mb-1">Failed to load tenants</p>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">All Sales Orgs</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tenants.length} total</p>
        </div>
        <button
          onClick={() => navigate('/tenants/new')}
          className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-500 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or owner email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <AdminListTable
        columns={lynxPaySalesOrgColumns()}
        rows={filtered}
        rowKey={(tenant) => tenant.id}
        onRowClick={(tenant) => navigate(`/tenants/${tenant.id}`)}
        emptyIcon={Building2}
        emptyMessage={
          search || statusFilter !== 'all'
            ? 'No sales organizations match your filters'
            : 'No tenants yet. Create your first tenant to get started.'
        }
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
      />
    </div>
  );
}

export default TenantList;
